import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { SigneeDocumentControllerApi } from '../../openapi/signature-api';
import ErrorService from '../../services/ErrorService';
import {
  IFrameEvent,
  IFrameEventEnum,
  OnDocumentAlreadySignedEventData,
  OnDocumentSignedEventData,
  SignatureWidgetModeEnum,
} from '../../types';
import {
  isReactNativeWebView,
  requestNativeFileDownload,
} from '../../utils/DeviceDetectionUtils';
import { getFileNameFromURL } from '../../utils/FileUtils';
import Logger from '../../utils/Logger';
import { getSignatureApiConfiguration } from '../../utils/OpenapiConfigurationUtils';
import { getWidgetUrl } from '../../utils/SignatureWidgetUtils';
import DefaultLoader from '../DefaultLoader';

interface SignatureWidgetProps {
  token: string;
  mode: SignatureWidgetModeEnum;
  onDocumentSigned?: (data: OnDocumentSignedEventData) => void;
  onDocumentAlreadySigned?: (data: OnDocumentAlreadySignedEventData) => void;
  isLoading: boolean;
  showDownload?: boolean;
  showMobileTooltip?: boolean;
  showWebHelperButtons?: boolean;
  browserMode?: boolean;
}

const SignatureWidget: React.FC<SignatureWidgetProps> = ({
  token,
  mode,
  onDocumentSigned,
  onDocumentAlreadySigned,
  isLoading,
  showDownload,
  showMobileTooltip,
  showWebHelperButtons,
  browserMode,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const onIframeEvent = useCallback(
    (e: IFrameEvent) => {
      switch (e.type) {
        case IFrameEventEnum.DOCUMENT_SIGNED:
          onDocumentSigned?.(e?.data);
          break;
        case IFrameEventEnum.DOCUMENT_ALREADY_SIGNED:
          onDocumentAlreadySigned?.(e?.data);
          break;
        case IFrameEventEnum.DOWNLOAD_DOCUMENT: {
          // Fetch URL from API and forward to React Native or handle in browser
          const fetchAndForward = async () => {
            try {
              const { data } = await new SigneeDocumentControllerApi(
                getSignatureApiConfiguration(),
              ).getDocumentTemplateFileUrlFromToken(token);

              const url = data?.attachmentUrl;
              if (!url) {
                throw new Error('Document URL not found');
              }

              // Extract filename from URL
              const filename = getFileNameFromURL(url) || 'document.pdf';

              // Forward to React Native WebView with url and filename
              const nativeDownloadSuccess = requestNativeFileDownload(
                url,
                filename,
                'DOWNLOAD_DOCUMENT',
              );

              if (nativeDownloadSuccess) {
                // Native app will handle the download
                return;
              }
            } catch (error) {
              ErrorService.notifyIgnoreAuthErrors(
                'Failed to fetch document URL for download',
                error as Error,
              );
            }
          };

          if (isReactNativeWebView()) {
            fetchAndForward();
          }
          break;
        }
        case IFrameEventEnum.OPEN_DOCUMENT_IN_BROWSER: {
          const browserUrl = getWidgetUrl(mode, token, {
            showDownload: true,
            showMobileTooltip: isMobile,
            showWebHelperButtons: false,
            browserMode: true,
          });
          window.open(browserUrl, '_blank');
          break;
        }
        default:
          const error = new Error(
            'Unrecognized event type in signature widget iframe: ' + e.type,
          );
          ErrorService.notify(error.message, error, {
            frameEvent: e,
          });
      }
    },
    [onDocumentSigned, onDocumentAlreadySigned, mode, token],
  );

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      const iframeData = e?.data;
      if (iframeRef?.current && typeof iframeData === 'string') {
        try {
          const iframeEvent: IFrameEvent = JSON.parse(iframeData);
          if (
            iframeEvent?.type === IFrameEventEnum.DOCUMENT_SIGNED ||
            iframeEvent?.type === IFrameEventEnum.DOCUMENT_ALREADY_SIGNED ||
            iframeEvent?.type === IFrameEventEnum.OPEN_DOCUMENT_IN_BROWSER ||
            iframeEvent?.type === IFrameEventEnum.DOWNLOAD_DOCUMENT
          ) {
            onIframeEvent(iframeEvent);
          }
        } catch {
          Logger.debug('Could not parse iframe event data', iframeData);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onIframeEvent]);

  const url = useMemo(
    () =>
      getWidgetUrl(mode, token, {
        showDownload,
        showMobileTooltip,
        showWebHelperButtons,
        browserMode,
      }),
    [
      mode,
      token,
      showDownload,
      showMobileTooltip,
      showWebHelperButtons,
      browserMode,
    ],
  );

  if (isLoading || !token) return <DefaultLoader />;

  return (
    <iframe
      ref={iframeRef}
      title='signature-widget'
      id='signature-widget'
      src={url}
      className='w-full h-full'
    />
  );
};

export default SignatureWidget;
